//  @flow

import * as React from "react";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import {
  TouchableOpacity,
  Modal,
  View,
  I18nManager,
  ScrollView,
  Dimensions,
} from "react-native";

import Triangle from "./Triangle";
import BlurView from "../BlurView";
import { isWeb } from "../../util";
import getTooltipCoordinate from "./getTooltipCoordinate";
import Flex from "../Flex";
import { withThemeProps, Touchable } from "../../style";
import { useUpdateEffect } from "../../hooks";

type Props = {
  withPointer: boolean;
  popover: React.Element | void;
  height: number | string;
  width: number | string;
  containerStyle: any;
  pointerColor: string;
  pointerStyle: {};
  onClose: () => void;
  onOpen: () => void;
  withOverlay: boolean;
  overlayColor: string;
  backgroundColor: string;
  highlightColor: string;
  toggleWrapperProps: {};
  actionType: "press" | "longPress" | "none";
  hover?: boolean;
};

// class Tooltip extends React.Component<Props, State> {
//   state = {
//     isVisible: false,
//     yOffset: 0,
//     xOffset: 0,
//     elementWidth: 0,
//     elementHeight: 0,
//   };

//   renderedElement;
//   timeout;

//   componentDidMount() {
//     // wait to compute onLayout values.
//     this.timeout = setTimeout(this.getElementPosition, 500);
//   }

//   componentWillUnmount() {
//     clearTimeout(this.timeout);
//   }
// }

function Tooltip({
  theme,
  children,
  withPointer = true,
  popover,
  width = 350,
  wrapperWidth,
  maxWidth,
  containerStyle = {},
  containerProps = {},
  pointerStyle = {},
  onClose = () => {},
  onOpen = () => {},
  withOverlay = false,
  overlayColor = "rgba(0,0,0,0.2)",
  color = "surface:setAlpha:0.8",
  highlightColor = "transparent",
  toggleWrapperProps = {},
  actionType = "press",
  hover = false,
  useScrollView = false,
  visible,
  gap = 10,
  ...rest
}: Props) {
  if (maxWidth && maxWidth < width) width = maxWidth;
  const ref = React.useRef(null);
  const insets = useSafeAreaInsets();
  const [isVisible, setVisible] = React.useState(false);
  const [position, setPosition] = React.useState({
    yOffset: 0,
    xOffset: 0,
    elementWidth: 0,
    elementHeight: 0,
  });

  React.useEffect(() => {
    if (isVisible === true && isWeb === true) {
      onOpen?.();
    }
  }, [isVisible]);

  const getElementPosition = () => {
    ref.current &&
      ref.current.measureInWindow((pageOffsetX, pageOffsetY, width, height) => {
        setPosition({
          xOffset: pageOffsetX,
          yOffset: pageOffsetY,
          elementWidth: width,
          elementHeight: height,
        });
      });
  };

  React.useEffect(() => {
    getElementPosition();
  }, [ref.current]);

  useUpdateEffect(() => {
    if (isVisible === false) {
      onClose && onClose();
    }
  }, [isVisible]);

  const toggleTooltip = () => {
    getElementPosition();
    setVisible((prevState) => {
      return !prevState;
    });
  };

  useUpdateEffect(() => {
    if (visible !== undefined) {
      if (visible === false) {
        setVisible(false);
      } else {
        toggleTooltip();
      }
    }
  }, [visible]);

  const wrapWithAction = (actionType, children) => {
    if (visible !== undefined) return children;
    switch (actionType) {
      case "press":
        return (
          <Touchable
            onPress={toggleTooltip}
            activeOpacity={1}
            onMouseOver={hover ? () => toggleTooltip() : undefined}
            {...toggleWrapperProps}
          >
            {children instanceof Function
              ? children({ toggleTooltip })
              : children}
          </Touchable>
        );
      case "longPress":
        return (
          <Touchable
            onLongPress={toggleTooltip}
            activeOpacity={1}
            onMouseOver={hover ? () => toggleTooltip() : undefined}
            {...toggleWrapperProps}
          >
            {children instanceof Function
              ? children({ toggleTooltip })
              : children}
          </Touchable>
        );
      default:
        return children;
    }
  };

  const getTooltipStyle = () => {
    const { yOffset, xOffset, elementHeight, elementWidth } = position;

    const Screen = Dimensions.get("window");

    const { x, y } = getTooltipCoordinate(
      xOffset,
      yOffset,
      elementWidth,
      elementHeight,
      Screen.width,
      Screen.height,
      width,
      withPointer
    );

    const pastMiddleLine = yOffset > y;

    const maxHeight = pastMiddleLine
      ? yOffset - 20 - insets.top
      : theme.height - (yOffset + elementHeight + 20 + insets.bottom);
    const tooltipStyle = {
      position: "absolute",
      left: I18nManager.isRTL ? null : x,
      right: I18nManager.isRTL ? x : null,
      width: width > theme.width ? theme.width - 20 : width,
      maxHeight,
      // default styles
      display: "flex",
      flex: 1,
      borderRadius: 10,
      ...containerStyle,
    };

    if (pastMiddleLine) {
      tooltipStyle.bottom = Screen.height - y;
    } else {
      tooltipStyle.top = y;
    }

    return tooltipStyle;
  };

  const renderPointer = (pastMiddleLine) => {
    const { yOffset, xOffset, elementHeight, elementWidth } = position;

    return (
      <View
        style={{
          position: "absolute",
          top: pastMiddleLine ? yOffset - 13 : yOffset + elementHeight - 2,
          left: I18nManager.isRTL ? null : xOffset + elementWidth / 2 - 7.5,
          right: I18nManager.isRTL ? xOffset + elementWidth / 2 - 7.5 : null,
        }}
      >
        <Triangle
          bg={color}
          style={{
            borderBottomColor: color,
            ...pointerStyle,
          }}
          isDown={pastMiddleLine}
        />
      </View>
    );
  };

  const renderContent = (withTooltip) => {
    if (!withTooltip) return wrapWithAction(actionType, children);

    const { yOffset, xOffset, elementWidth, elementHeight } = position;
    const tooltipStyle = getTooltipStyle();
    const Wrapper = useScrollView ? ScrollView : View;
    return (
      <>
        {withOverlay ? (
          <View
            style={{
              position: "absolute",
              top: yOffset,
              left: I18nManager.isRTL ? null : xOffset,
              right: I18nManager.isRTL ? xOffset : null,
              backgroundColor: highlightColor,
              overflow: "visible",
              width: elementWidth,
              height: elementHeight,
            }}
            // onMouseLeave={hover ? () => toggleTooltip() : undefined}
          >
            {children instanceof Function
              ? children({ toggleTooltip })
              : children}
          </View>
        ) : null}
        {withPointer && renderPointer(!tooltipStyle.top)}

        <Touchable
          onPress={() => {}}
          activeOpacity={1}
          bg={color}
          style={tooltipStyle}
          onMouseLeave={hover ? () => toggleTooltip() : undefined}
          shadow={50}
          borderWidth={1}
          borderColor="primary:setAlpha:0.2"
          {...rest}
        >
          <Flex borderRadius={tooltipStyle.borderRadius} overflow="hidden">
            <BlurView intensity={30}>
              <Wrapper
                {...(useScrollView
                  ? {
                      style: {
                        flex: 1,
                        maxHeight: tooltipStyle.maxHeight,
                      },
                      contentContainerStyle: { flexGrow: 1 },
                      showsVerticalScrollIndicator: false,
                    }
                  : {})}
              >
                <Flex w="100%" collapsed p={gap}>
                  {popover instanceof Function
                    ? popover({ toggleTooltip })
                    : popover}
                </Flex>
              </Wrapper>
            </BlurView>
          </Flex>
        </Touchable>
      </>
    );
  };

  return (
    <Flex collapsable={false} w={wrapperWidth} ref={ref}>
      {renderContent(false)}
      <Modal
        animationType="fade"
        visible={isVisible}
        transparent
        onDismiss={onClose}
        onShow={onOpen}
        onRequestClose={onClose}
      >
        <TouchableOpacity
          style={{
            flex: 1,
            backgroundColor: withOverlay
              ? overlayColor
                ? overlayColor
                : "rgba(250, 250, 250, 0.70)"
              : "transparent",
          }}
          onPress={toggleTooltip}
          activeOpacity={1}
        >
          {renderContent(true)}
        </TouchableOpacity>
      </Modal>
    </Flex>
  );
}

export default withThemeProps(Tooltip, "Tooltip");
